import { Component, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Dictionary } from '@ngrx/entity';
import { sleepForMS } from 'src/app/helpers/helpers';
import { DropdownOption, VenueSelection } from 'globals/store/programs.data';
import { DefaultTabName } from 'globals/store/share.data';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { TitleToggleComponent } from '../wrappers/title-toggle-wrapper.component';
import { CommonModule } from '@angular/common';
import { MutatingInputComponent } from '../joi-input/mutating-input.component';
import { MatRadioModule } from '@angular/material/radio';
import { ExpandingWrapperComponent } from '../wrappers/expanding-wrapper.component';
import { DropdownComponent } from '../misc/dropdown.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogWrapperComponent } from '../wrappers/dialog-wrapper.component';
import { SetsEqualPipe } from 'src/app/services/pipes/arraysEqual.pipe';
import { FormsModule } from '@angular/forms';


@Component({
    selector: 'nested-checks',
    templateUrl: './nested-checks.html',
    styleUrls: ['./nested-checks.dialog.scss'],
    standalone: true,
    imports: [TitleToggleComponent, CommonModule, MutatingInputComponent, MatRadioModule, ExpandingWrapperComponent, DropdownComponent, MatCheckboxModule, DialogWrapperComponent, SetsEqualPipe, FormsModule]
})
export class NestedChecksDialogComponent {

  deselectedIds: Set<string> = new Set()
  missingSomeChildren: Set<string> = new Set()
  parents: { [checkId: string]: NestedCheck } = {}
  usedIds: Set<string> = new Set()
  eldestEndNodes: Set<string> = new Set() // "eldest" among their siblings only, when siblings are all end nodes
  collapseStates = {}
  tabNames: Dictionary<string> = {}
  values = {}
  initialized = false

  constructor(public dialogRef: MatDialogRef<NestedChecksDialogData>,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: NestedChecksDialogData,
  ) {
    const { deselectedIds, nestedCheckGroups, tabNameMap } = data
    this.deselectedIds = new Set(deselectedIds)
    this.initializeNestedCheckGroups(nestedCheckGroups)
    // this.eldestEndNodes.forEach(this.partialCheckCheck.bind(this))
    if (tabNameMap) {
      this.tabNames = jsonCopyObject(tabNameMap)
      nestedCheckGroups.filter(n => !!n.tabId).forEach(n => {
        if (!this.tabNames[n.tabId]) this.tabNames[n.tabId] = n.defaultTabName
      })
    }
    if (this.data.currentValues) this.values = jsonCopyObject(this.data.currentValues)
    checkIds(data.nestedCheckGroups)
    // console.log({ tabNameMap })
  }

  updateValue(type: string, value: any) {
    this.values[type] = value
  }

  initializeNestedCheckGroups(nestedCheckGroups: NestedCheckGroup[]) {
    if (!nestedCheckGroups) return
    nestedCheckGroups.filter(g => g.checkboxes).forEach(nestedCheckGroups => nestedCheckGroups.checkboxes.forEach(nestedCheck => {
      this.checkIds(nestedCheck)
      this.addParents(nestedCheck)
      if (this.deselectedIds.has(nestedCheck.id)) this.uncheck(nestedCheck) // <== uncheck recursively down
    }))

  }

  checkInitialized() {
    if (this.initialized) return
    setTimeout(() => this.initialized = true, 0)
  }

  toggleTab(tabId: string) {
    if (this.deselectedIds.has(tabId)) this.deselectedIds.delete(tabId)
    else this.deselectedIds.add(tabId)
    this.updateSet()
  }


  checkIds(nestedCheck: NestedCheck) { // recursive down
    if (this.usedIds.has(nestedCheck.id)) { // <== deleting this check is not the way to solve this problem.
      throw Error(`The id [${nestedCheck.id}] has already been used.  Please 🦀🦀🦀 it`)
    }
    this.usedIds.add(nestedCheck.id)
    nestedCheck.children && nestedCheck.children.forEach(this.checkIds.bind(this))
  }

  addParents(parent: NestedCheck) {
    if (parent.children) {
      if (parent.children.every(child => !child.children)) this.eldestEndNodes.add(parent.children[0].id)
      parent.children.forEach(child => {
        this.parents[child.id] = parent
        this.addParents(child)
      })
    }
  }

  check(nestedCheck: NestedCheck) { // <== recursive down
    this.deselectedIds.delete(nestedCheck.id)
    this.missingSomeChildren.delete(nestedCheck.id)
    nestedCheck.disabled = false
    nestedCheck.children && nestedCheck.children.forEach(this.enable.bind(this))
  }

  enable(nestedCheck: NestedCheck) {
    nestedCheck.disabled = false
    nestedCheck.children && nestedCheck.children.forEach(this.enable.bind(this))
  }

  disable(nestedCheck: NestedCheck) {
    nestedCheck.disabled = true
    nestedCheck.children && nestedCheck.children.forEach(this.disable.bind(this))
  }

  uncheck(nestedCheck: NestedCheck, disable = false) { // <== recursive down
    this.deselectedIds.add(nestedCheck.id)
    this.missingSomeChildren.delete(nestedCheck.id)
    nestedCheck.children && nestedCheck.children.forEach(this.disable.bind(this))
  }

  onRadioChange(id: string, radioGroup: NestedRadioGroup) {

    radioGroup.choices.forEach(c => this.deselectedIds.delete(c.id))

    if (id !== 'default') this.deselectedIds.add(id)
    this.data.radioChoices[radioGroup.id] = id
    console.log({ id, radioGroup, deselectedIds: this.deselectedIds })
  }

  async toggleNested(nestedCheck: NestedCheck) {
    if (this.missingSomeChildren.has(nestedCheck.id) || !this.deselectedIds.has(nestedCheck.id)) {
      this.uncheck(nestedCheck)
    } else {
      this.check(nestedCheck)
    }
    this.updateSet()
  }

  async updateSet() {
    await sleepForMS(50) 
    this.deselectedIds = new Set(this.deselectedIds)
    this.changeDetectorRef.detectChanges()
  }

  cancel() {
    this.dialogRef.close()
  }

  useView(view: NestedChecksView) {
    this.deselectedIds = new Set(view.deselectedIdMaker())
  }

  selectAll() {
    this.deselectedIds = new Set()
    this.updateSet()
  }

  unselectAll( ) {
    this.deselectedIds = new Set()
    this.data.nestedCheckGroups.forEach(g => {
      if (g.tabId) this.toggleTab(g.tabId)
      if (g.checkboxes) g.checkboxes.forEach(c => this.uncheck(c))
      if (g.children) g.children.forEach(cg => {
        if (cg.checkboxes) cg.checkboxes.forEach(c => this.uncheck(c))
      })
      if (g.endingTabDropdown) this.toggleTab(g.endingTabDropdown.id)

    })
    this.updateSet()
  }

  selectGroup(group: NestedCheckGroup) {
    if (group.checkboxes) group.checkboxes.forEach(c => {
      this.check(c)
      if (c.children) c.children.forEach(c => this.check(c))
    })
    if (group.children) group.children.forEach(g => this.selectGroup(g))
  }

  unselectGroup(group: NestedCheckGroup) {
    if (group.checkboxes) group.checkboxes.forEach(c => {
      this.uncheck(c)
      if (c.children) c.children.forEach(c => this.uncheck(c))
    })
    if (group.children) group.children.forEach(g => this.unselectGroup(g))
  }

  submit() {
    const returnData: NestedChecksReturnData = {
      deselectedIds: Array.from(this.deselectedIds),
      tabNameMap: this.tabNames,
      valueSelections: this.values
    }
    // console.log({ returnData })
    this.dialogRef.close(returnData)
  }

}

export interface NestedCheck {
  label: string // titles optional, especially for children
  id: string
  disabled?: boolean
  children?: NestedCheck[]
}


export type NCGEndingTabType = 'primaryVenue'
export interface NCGEndingTabDropdown {
  title: string
  id: NCGEndingTabType
  currentSelection: DropdownOption
  toggleOptions: string[]
  dropdownTitle: string
  dropdownOptions: DropdownOption[]
}


export interface NestedRadioGroup {
  id: string // 
  choices: { id: string, label: string }[]
}

export interface NestedCheckGroup {
  groupName?: string
  radios?: NestedRadioGroup[]
  checkboxes?: NestedCheck[]
  tabId?: string
  hideId?: string
  defaultTabName?: DefaultTabName
  noTabName?: boolean
  hintText?: string
  topHint?: string
  children?: NestedCheckGroup[]
  endingTabDropdown?: NCGEndingTabDropdown // If I had known we would squeeze so many different components into the nested checks dialog I would have not made a general dialog for it
  instructions?: { title?: string; text?: string; }
}

export interface NestedChecksReturnData {
  deselectedIds: string[]
  tabNameMap: { [tId: string]: string }
  valueSelections?: { [id:string]: any }
}

export interface NestedChecksView<T = string> {
  title: string
  id: T
  deselectedIdMaker: () => string[],
  ids: Set<string> // needed for checking if active, don't pull ids from here
}

export interface NestedChecksDialogData {
  title: string
  instructions?: string
  successLabel: string
  backgroundColor: string
  deselectedIds: string[]
  currentValues?: { [id:string]: string }
  tabNameMap?: { [tId: string]: string }
  radioChoices?: { [rId: string]: string }
  nestedCheckGroups: NestedCheckGroup[],
  views?: NestedChecksView[]
}

const addId = (idsArray: Set<string>, id: string) => {
  if (!id) return
  if (idsArray.has(id)) {
    console.log(`DUPLICATE ID IN DISPLAY OPTIONS! (${id}) THIS IS NOT OK!`)
  }
  idsArray.add(id)
}

const addIds = (idsArray: Set<string>, { id, children }: NestedCheck) => {
  addId(idsArray, id)
  children && children.forEach(child => addIds(idsArray,child))
}

const checkIds = (nestedCheckGroups: NestedCheckGroup[]) => {
  const fieldIds: Set<string> = new Set()
  nestedCheckGroups.forEach(g => addId(fieldIds, g.tabId))
  nestedCheckGroups.filter(g => g.endingTabDropdown).forEach(g => addId(fieldIds, g.endingTabDropdown.id))
  nestedCheckGroups.filter(g => g.checkboxes).forEach(({ checkboxes }) => checkboxes.forEach(child => addIds(fieldIds, child)))
}
